import { ExternalLink } from 'lucide-react'
import { DateField } from '@internal/ui-kit/components/admin'
import { Badge } from '@internal/ui-kit/components/ui/badge'
import { Button } from '@internal/ui-kit/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@internal/ui-kit/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@internal/ui-kit/components/ui/tooltip'
import { useSpecialistLookup } from '@/components/app/useSpecialists'
import { fromApiUtc } from '@internal/ui-kit/lib/fromApiUtc'
import { CreateEventDrawer } from './CreateEventDrawer'
import { useUserEvents } from './useUserProfileData'

const MARKETPLACE_BASE_URL = import.meta.env.VITE_MARKETPLACE_BASE_URL

/** Same sticky-last-column treatment `apps/orders`' `OrderEventsSection.tsx` already uses for its own wide events table (Alex's ticket-comment feedback there: the row action was scrolled far off-screen). */
const STICKY_ACTIONS_CLASS = 'sticky right-0 bg-background'

/** Source table's `_.startCase` override on `location_type` (e.g. `by_appointment` -> `By Appointment`). */
function startCase(value: string | null | undefined): string {
  if (!value) return '—'
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * The combined page's "Appointments" tab (HATCH-101, `getUserEvents` —
 * `user-profile`'s "events" `Screen`, `table1` — plus
 * `drawerFrame1CreateEvent`, booking a new appointment). Split out of the
 * original combined "Orders & appointments" tab into its own tab per ticket
 * feedback (round 4).
 *
 * Round 10 (HATCH-203): columns now match `table1`'s full non-hidden set
 * (`retool-exports/user-profile/components.json`) instead of a trimmed
 * subset, and each row has an "Open consultation form" button —
 * `table1`'s own `386b9` row action, which opens
 * `{marketplace}/specialists/appointments/{event_id}` in a new tab
 * (`openUrl`, `retool-exports/user-profile/components.json`'s `clickAction`
 * event; the source hardcodes the staging/production marketplace hosts, but
 * this repo has no per-environment host table, so `VITE_MARKETPLACE_BASE_URL`
 * is a new build-time env var instead — falls back to a relative URL, same
 * origin as the admin app, if unset).
 *
 * Round 11 (HATCH-203 follow-up): the action button is now icon-only (with a
 * tooltip) and sticky on the table's trailing edge, same treatment
 * `apps/orders`' `OrderEventsSection.tsx` already gives its own wide
 * events table's row action — a per-ticket-comment ask, "can open
 * consultation form button be sticky, and be icon kind of button", plus a
 * path correction: the real consultation-form route is
 * `/specialists/appointments/{event_id}`, not `/consultations/{event_id}`.
 */
export function AppointmentsTab({ userId }: { userId: string }) {
  const { events, isPending, refetch } = useUserEvents(userId)
  const specialistLabelFor = useSpecialistLookup()

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">Appointments</h4>
        <CreateEventDrawer userId={userId} onCreated={refetch} />
      </div>
      {isPending ? (
        <p className="text-muted-foreground text-sm">Loading appointments…</p>
      ) : events.length === 0 ? (
        <p className="text-muted-foreground text-sm">No appointments for this user.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Specialist</TableHead>
                <TableHead>Location name</TableHead>
                <TableHead>Location type</TableHead>
                <TableHead>Location address</TableHead>
                <TableHead>Meeting URL</TableHead>
                <TableHead>Form slug</TableHead>
                <TableHead>PDF template type</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>End</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Order ID</TableHead>
                <TableHead>Created at</TableHead>
                <TableHead className={STICKY_ACTIONS_CLASS} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.event_id}>
                  <TableCell>{event.title ?? '—'}</TableCell>
                  <TableCell>{specialistLabelFor(event.owner_id)}</TableCell>
                  <TableCell>{event.location_name ?? '—'}</TableCell>
                  <TableCell>{startCase(event.location_type)}</TableCell>
                  <TableCell>{event.location_address ?? '—'}</TableCell>
                  <TableCell>
                    {event.meeting_url ? (
                      <a
                        href={event.meeting_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:no-underline"
                      >
                        Join
                      </a>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{event.form_slug ?? '—'}</TableCell>
                  <TableCell>{event.pdf_template_type ?? '—'}</TableCell>
                  <TableCell>
                    {event.start_time ? <DateField record={event} source="start_time" showTime transform={fromApiUtc} /> : '—'}
                  </TableCell>
                  <TableCell>
                    {event.end_time ? <DateField record={event} source="end_time" showTime transform={fromApiUtc} /> : '—'}
                  </TableCell>
                  <TableCell>
                    {event.canceled_at ? (
                      <Badge variant="destructive">Canceled</Badge>
                    ) : event.is_completed ? (
                      <Badge variant="outline">Completed</Badge>
                    ) : event.confirmed_at ? (
                      <Badge variant="secondary">Confirmed</Badge>
                    ) : (
                      <Badge variant="secondary">Pending</Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{event.order_id ?? '—'}</TableCell>
                  <TableCell>
                    {event.created_at ? <DateField record={event} source="created_at" showTime transform={fromApiUtc} /> : '—'}
                  </TableCell>
                  <TableCell className={STICKY_ACTIONS_CLASS}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button asChild type="button" size="icon" variant="secondary">
                          <a
                            href={`${MARKETPLACE_BASE_URL ?? ''}/specialists/appointments/${event.event_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open consultation form"
                          >
                            <ExternalLink className="size-4" />
                          </a>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Open consultation form</TooltipContent>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
